import {
  useId,
  useMemo,
  useState,
  type ChangeEvent,
  type FormEvent,
  type ReactElement,
} from 'react';
import { Link, useNavigate } from 'react-router-dom';
import type { TenantFeature } from '@kawai/shared';
import { Button, Input, cn } from '@kawai/ui';
import { useAuth } from '../lib/auth.js';
import { useTenantCreate } from '../lib/hooks/useTenantCreate.js';
import {
  tenantCreateErrorMessage,
  type TenantCreateInput,
} from '../lib/api/tenantCreate.js';
import { CARD, CARD_TITLE } from '../lib/uiClasses.js';
import { PageHeader } from '../components/PageHeader.js';
import { Notice } from '../components/Notice.js';

const ALL_FEATURES: TenantFeature[] = ['reservation', 'fuel', 'richmenu', 'promo'];

const TENANT_ID_REGEX = /^[a-z0-9-]+$/;
const HEX_COLOR_REGEX = /^#[0-9a-fA-F]{6}$/;

interface FormState {
  tenantId: string;
  name: string;
  shopName: string;
  features: Set<TenantFeature>;
  primaryColor: string;
  brandName: string;
  logoUrl: string;
}

const INITIAL_FORM: FormState = {
  tenantId: '',
  name: '',
  shopName: '',
  features: new Set(['reservation']),
  primaryColor: '',
  brandName: '',
  logoUrl: '',
};

/** features のチップ（チェックボックス入り）。選択 = ブランド色の面 / 未選択 = 灰面。枠線なし。 */
const FEATURE_CHIP_BASE =
  'inline-flex cursor-pointer items-center gap-2 rounded-full px-3.5 py-1.5 text-label font-medium';
const FEATURE_CHIP_ON = 'bg-brand-tint text-brand-ink';
const FEATURE_CHIP_OFF = 'bg-surface-2 text-ink-2 hover:bg-surface-3';

/**
 * S-06: 新規テナント作成フォーム。
 *
 * - tenantId / name / shopName / features (チェックボックス) / customizations (一部) を入力
 * - submit で `tenantCreate` Callable 呼出
 * - 成功で TenantsListPage (`/`) へ navigate（`useTenantCreate` 側で query invalidate 済み）
 * - エラーは `tenantCreateErrorMessage` で日本語化して表示
 * S-137: 見た目は docs/UI-GUIDE.md の v2（白カード 30/26・入力は @kawai/ui・fieldset/legend は
 * section + h3 に = 罫線を持たない）。
 */
export function TenantCreatePage() {
  const { email, signOut } = useAuth();
  const navigate = useNavigate();
  const mutation = useTenantCreate();

  const [form, setForm] = useState<FormState>(INITIAL_FORM);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const clientErrors = useMemo(() => validateForm(form), [form]);

  // 各フィールドの id を React 18 の useId() で安定生成（label の htmlFor とリンク）。
  const tenantIdId = useId();
  const nameId = useId();
  const shopNameId = useId();
  const primaryColorId = useId();
  const brandNameId = useId();
  const logoUrlId = useId();

  function handleFeatureToggle(f: TenantFeature) {
    setForm((prev) => {
      const next = new Set(prev.features);
      if (next.has(f)) next.delete(f);
      else next.add(f);
      return { ...prev, features: next };
    });
  }

  function handleField<K extends keyof FormState>(key: K) {
    return (e: ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;
      setForm((prev) => ({ ...prev, [key]: value }));
    };
  }

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setSubmitError(null);
    if (clientErrors.length > 0) {
      // クライアントエラーはフォーム下部に一覧表示済み（送信ボタンも disabled）。
      return;
    }
    const customizations: TenantCreateInput['customizations'] = {};
    if (form.primaryColor) customizations.primaryColor = form.primaryColor;
    if (form.brandName) customizations.brandName = form.brandName;
    if (form.logoUrl) customizations.logoUrl = form.logoUrl;
    const payload: TenantCreateInput = {
      tenantId: form.tenantId.trim(),
      name: form.name.trim(),
      shopName: form.shopName.trim(),
      features: Array.from(form.features),
      ...(Object.keys(customizations).length > 0 ? { customizations } : {}),
    };
    try {
      await mutation.mutateAsync(payload);
      navigate('/');
    } catch (err) {
      setSubmitError(tenantCreateErrorMessage(err));
    }
  }

  const submitting = mutation.isPending;

  return (
    <main className="mx-auto max-w-[760px] p-6">
      <PageHeader
        title="テナントを追加"
        description="新規顧客のオンボーディング（S-06）"
        email={email}
        links={[{ to: '/', label: '← 一覧へ戻る' }]}
        onSignOut={() => {
          void signOut();
        }}
      />

      <form
        onSubmit={handleSubmit}
        className={`mt-6 flex flex-col gap-5 ${CARD}`}
        aria-label="テナント追加フォーム"
      >
        <h2 className={CARD_TITLE}>基本情報</h2>
        <Field
          label="tenantId"
          htmlFor={tenantIdId}
          hint="kebab-case（小文字英数字とハイフンのみ / 例: kawai-takefu21ss）"
        >
          <Input
            id={tenantIdId}
            type="text"
            value={form.tenantId}
            onChange={handleField('tenantId')}
            placeholder="kawai-takefu21ss"
            autoComplete="off"
            required
            disabled={submitting}
          />
        </Field>

        <Field
          label="正式名称 (name)"
          htmlFor={nameId}
          hint="管理画面に表示されるテナント正式名（例: カワイ株式会社）"
        >
          <Input
            id={nameId}
            type="text"
            value={form.name}
            onChange={handleField('name')}
            placeholder="カワイ株式会社"
            required
            disabled={submitting}
          />
        </Field>

        <Field
          label="店舗名 (shopName)"
          htmlFor={shopNameId}
          hint="顧客 UI のヘッダ等に表示（例: セルフ武生21SS）"
        >
          <Input
            id={shopNameId}
            type="text"
            value={form.shopName}
            onChange={handleField('shopName')}
            placeholder="セルフ武生21SS"
            required
            disabled={submitting}
          />
        </Field>

        <section aria-label="features">
          <h3 className="text-em font-semibold text-ink">features（最低 1 つ選択）</h3>
          <div className="mt-3 flex flex-wrap gap-2">
            {ALL_FEATURES.map((f) => {
              const checked = form.features.has(f);
              return (
                <label
                  key={f}
                  className={cn(
                    FEATURE_CHIP_BASE,
                    checked ? FEATURE_CHIP_ON : FEATURE_CHIP_OFF,
                    submitting && 'cursor-not-allowed opacity-50',
                  )}
                >
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => handleFeatureToggle(f)}
                    disabled={submitting}
                    className="accent-brand"
                  />
                  {f}
                </label>
              );
            })}
          </div>
        </section>

        <section aria-label="customizations" className="flex flex-col gap-4">
          <h3 className="text-em font-semibold text-ink">customizations（任意）</h3>
          <Field
            label="primaryColor"
            htmlFor={primaryColorId}
            hint="例: #1A73E8（#RRGGBB 形式）"
          >
            <Input
              id={primaryColorId}
              type="text"
              value={form.primaryColor}
              onChange={handleField('primaryColor')}
              placeholder="#1A73E8"
              disabled={submitting}
            />
          </Field>
          <Field
            label="brandName"
            htmlFor={brandNameId}
            hint="顧客 UI のブランド表示（未設定なら shopName を使用）"
          >
            <Input
              id={brandNameId}
              type="text"
              value={form.brandName}
              onChange={handleField('brandName')}
              placeholder="武生21SS"
              disabled={submitting}
            />
          </Field>
          <Field label="logoUrl" htmlFor={logoUrlId} hint="任意の CDN / GCS の URL">
            <Input
              id={logoUrlId}
              type="url"
              value={form.logoUrl}
              onChange={handleField('logoUrl')}
              placeholder="https://example.com/logo.png"
              disabled={submitting}
            />
          </Field>
        </section>

        {submitError && <Notice role="alert">{submitError}</Notice>}
        {!submitError && clientErrors.length > 0 && (
          <ul
            className="flex list-disc flex-col gap-1 pl-5 text-label text-danger-ink"
            aria-live="polite"
          >
            {clientErrors.map((msg) => (
              <li key={msg}>{msg}</li>
            ))}
          </ul>
        )}

        <div className="flex flex-wrap items-center gap-3">
          <Button type="submit" disabled={submitting || clientErrors.length > 0}>
            {submitting ? '作成中…' : 'テナントを作成'}
          </Button>
          <Link to="/" className="text-body text-ink-3" aria-disabled={submitting}>
            キャンセル
          </Link>
        </div>
      </form>
    </main>
  );
}

// ---------- 入力検証（クライアント側プリチェック） ----------
// Functions 側 zod が最終バリデーション。ここはユーザー体験用の即時表示のみ。
function validateForm(f: FormState): string[] {
  const errors: string[] = [];
  const tenantId = f.tenantId.trim();
  // tenantId は「必須 → 文字数 → 文字種」を 1 つずつ（同一フィールドで重複表示しない）。
  if (!tenantId) {
    errors.push('tenantId は必須です');
  } else if (tenantId.length > 64) {
    errors.push('tenantId は 64 文字以内で入力してください');
  } else if (!TENANT_ID_REGEX.test(tenantId)) {
    errors.push('tenantId は小文字英数字とハイフンのみ使用できます');
  }
  if (!f.name.trim()) errors.push('name は必須です');
  if (!f.shopName.trim()) errors.push('shopName は必須です');
  if (f.features.size === 0) errors.push('features を最低 1 つ選択してください');
  if (f.primaryColor && !HEX_COLOR_REGEX.test(f.primaryColor)) {
    errors.push('primaryColor は #RRGGBB 形式で入力してください');
  }
  if (f.logoUrl) {
    try {
      new URL(f.logoUrl);
    } catch {
      errors.push('logoUrl は URL 形式で入力してください');
    }
  }
  return errors;
}

// ---------- 小さい layout コンポーネント ----------

interface FieldProps {
  label: string;
  htmlFor: string;
  hint?: string;
  children: ReactElement;
}

function Field({ label, htmlFor, hint, children }: FieldProps) {
  return (
    <div className="flex flex-col gap-1.5">
      <label htmlFor={htmlFor} className="text-label font-medium text-ink-2">
        {label}
      </label>
      {children}
      {hint && <span className="text-note text-ink-3">{hint}</span>}
    </div>
  );
}
